import React, { useMemo } from "react";
import {
  buildDefaultEmailBuilderState,
  createEmailBuilderBlock,
  createEventItem,
  renderEmailBuilderBlock,
  type EmailBuilderBlock,
  type EmailBuilderBlockType,
  type EmailBuilderPerkItem,
  type EmailFooterProfile,
} from "./emailBuilderData";

type EmailBlockThumbnailProps = {
  type: EmailBuilderBlockType;
};

// D22 EC-23 / design (16) palette pvList: each thumbnail is the real block
// render at 420 px, scaled to half size. Data blocks get sample content (the
// design placeholders). Links become plain text so nothing interactive sits
// inside the palette button, and data- markers are removed.
const samplePerk: EmailBuilderPerkItem = {
  id: "sample-perk", title: "Perk Title", company: "Member Company Name", logoUrl: "",
  description: "The perk description will show here.", redeem: "In Person", validUntil: "Valid Until Date",
  createdAtUtcMilli: null,
};

const sampleEvent = {
  date: "Tuesday, June 24, 2025",
  time: "11:30 AM \u2013 1:30 PM",
  location: "Harbour View Event Centre\n123 Harbour Dr., Sand Bay",
};

const sampleBlock = (type: EmailBuilderBlockType): EmailBuilderBlock => {
  const block = createEmailBuilderBlock(type);
  switch (block.type) {
    case "upcomingEventsList":
      block.settings.events = [createEventItem({ eventTitle: "Event Name", date: sampleEvent.date, time: sampleEvent.time, location: "Venue", description: "Details" })];
      break;
    case "eventAnnouncement":
      // Monday 13390254380 (S13): the design palette shows a sample event,
      // not the field names (CMP Dashboard (24) thumbnail for eventAnn).
      block.settings.events = [createEventItem({ eventTitle: "Event Name", ...sampleEvent })];
      break;
    case "newsroomHighlights":
      block.settings.items = [{ id: "sample-news", title: "News Title", linkUrl: "", imageUrl: "", category: "News Type", publishedAtUtcMilli: null, author: "By Author Name", readTime: "Read Time", sourceAttribution: "" }];
      break;
    case "newMembers":
      block.settings.items = ["Member Name", "Member Name", "Member Name"].map((name, index) => ({ id: `sample-member-${index}`, name, logoUrl: "", category: "Category", joinedAtUtcMilli: null, linkUrl: "" }));
      break;
    case "perks":
      block.settings.items = [samplePerk];
      break;
    case "perkWeek":
      // Design (19) palette: no perk yet, so the renderer shows the design
      // placeholder perk ("Perk Title", "Member Logo") in the narrow layout.
      block.settings.perk = null;
      break;
    case "logoDate":
      block.settings.date = "Month DD, YYYY";
      break;
    default:
      break;
  }
  return block;
};

const thumbnailTheme = (() => {
  const state = buildDefaultEmailBuilderState();
  return { ...state.theme, fontFamily: "DM Sans" };
})();

// Social Media Icons render the chamber's own links; the sample shows the
// design's four (Facebook, Instagram, LinkedIn, X).
const sampleProfile: EmailFooterProfile = {
  name: "Chamber", facebook: "chamber", instagram: "chamber", linkedin: "company/chamber", twitter: "chamber",
};

export const renderEmailBlockThumbnailHtml = (type: EmailBuilderBlockType): string =>
  // The Email Signature sample shows "Chamber Name" and a dashed "Chamber
  // Logo" box (design (19) palette thumbnail).
  renderEmailBuilderBlock(sampleBlock(type), thumbnailTheme, sampleProfile, { samplePlaceholders: true })
    .replace(/<a\b[^>]*>/gi, "<span>")
    .replace(/<\/a>/gi, "</span>")
    // No send markers (unsubscribe, New Members, tracking) in the palette.
    .replace(/\sdata-[a-z0-9-]+(="[^"]*")?/gi, "");

export const EmailBlockThumbnail = ({ type }: EmailBlockThumbnailProps) => {
  const html = useMemo(() => renderEmailBlockThumbnailHtml(type), [type]);
  return (
    <div aria-hidden="true" className="pointer-events-none overflow-hidden p-1">
      <div
        style={{ width: 420, zoom: 0.5 }}
        dangerouslySetInnerHTML={{ __html: html }}
      />
    </div>
  );
};

export default EmailBlockThumbnail;
